// Hook de tema claro/oscuro para el admin-portal.
//
// Comportamiento:
//   - Lee preferencia explicita del usuario de localStorage (clave
//     `authsys-admin-color-scheme`). Si no hay, usa prefers-color-scheme.
//   - Aplica/saca la clase `dark` en <html> (Tailwind con darkMode: 'class').
//   - Escucha cambios del system; solo afectan el render si el usuario no
//     fijo una preferencia explicita.
//
// FOUC: el flash inicial se evita con un snippet inline en index.html que
// aplica la clase ANTES que React monte. Este hook se sincroniza despues.

import { useCallback, useEffect, useState } from 'react';

export type ColorScheme = 'light' | 'dark';
export type ColorSchemePreference = ColorScheme | 'system';

const STORAGE_KEY = 'authsys-admin-color-scheme';

function getSystemScheme(): ColorScheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readPreference(): ColorSchemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark') return v;
  return 'system';
}

function applyClass(scheme: ColorScheme): void {
  const root = document.documentElement;
  if (scheme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

interface UseColorSchemeResult {
  colorScheme: ColorScheme;
  preference: ColorSchemePreference;
  setPreference: (p: ColorSchemePreference) => void;
  toggle: () => void;
}

export function useColorScheme(): UseColorSchemeResult {
  const [preference, setPreferenceState] = useState<ColorSchemePreference>(() => readPreference());
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(() => getSystemScheme());

  const colorScheme: ColorScheme = preference === 'system' ? systemScheme : preference;

  useEffect(() => {
    applyClass(colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      setSystemScheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setPreference = useCallback((p: ColorSchemePreference) => {
    setPreferenceState(p);
    try {
      if (p === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // localStorage bloqueado (modo privado / cookies disabled) — solo runtime.
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(colorScheme === 'dark' ? 'light' : 'dark');
  }, [colorScheme, setPreference]);

  return { colorScheme, preference, setPreference, toggle };
}
